import React, { useEffect, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { updateSubscriptionInfo } from '../../store/slices/authSlice';
import { useGetSubscriptionStatusQuery } from '../../services/stripeApi';
import { useGetUserLimitInfoQuery } from '../../services/usersApi';
import { isPaidPlan } from '../../constants/moduleKeys';

interface SubscriptionSyncProviderProps {
  children: ReactNode;
}

/**
 * SubscriptionSyncProvider - Syncs subscription status from Stripe with Redux state
 *
 * This provider should wrap all authenticated routes to ensure subscription status
 * is always up-to-date, even if webhooks haven't updated the database yet.
 *
 * It queries Stripe directly through the backend API to get the real subscription
 * status and updates Redux accordingly.
 */
const SubscriptionSyncProvider: React.FC<SubscriptionSyncProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, accessToken, plan: currentPlan, isTrialExpired: currentIsTrialExpired } = useAppSelector(state => state.auth);

  // Fetch subscription status from Stripe
  const { data: subscriptionStatus, isLoading: loadingSubscription } = useGetSubscriptionStatusQuery(undefined, {
    skip: !isAuthenticated || !accessToken,
    // Refetch on mount and when window regains focus
    refetchOnMountOrArgChange: true,
  });

  // Fetch user limit info for additional subscription data
  const { data: limitInfo, isLoading: loadingLimitInfo } = useGetUserLimitInfoQuery(undefined, {
    skip: !isAuthenticated || !accessToken,
    refetchOnMountOrArgChange: true,
  });

  // Sync subscription status with Redux state
  useEffect(() => {
    if (!isAuthenticated || loadingSubscription || loadingLimitInfo) return;
    if (!subscriptionStatus || !limitInfo) return;

    const stripePlanName = subscriptionStatus.planName;
    const stripeStatus = subscriptionStatus.status;

    // Determine if subscription is active based on Stripe status
    const isStripeActive = stripeStatus === 'active' || stripeStatus === 'trialing';
    const isPaid = isPaidPlan(stripePlanName);

    // Calculate isTrialExpired based on Stripe data and internal trial
    // If Stripe says subscription is active, trial is NOT expired
    // Also check internal trial period (managed without Stripe)
    const isTrialStillValid = limitInfo.trialEndDate && new Date(limitInfo.trialEndDate) > new Date();
    const isTrialExpiredFromStripe = !isPaid && !isStripeActive && !isTrialStillValid;

    // Check if we need to update Redux state
    const planChanged = currentPlan !== stripePlanName;
    const trialExpiredChanged = currentIsTrialExpired !== isTrialExpiredFromStripe;

    if (planChanged || trialExpiredChanged) {
      dispatch(updateSubscriptionInfo({
        plan: stripePlanName,
        isTrialExpired: isTrialExpiredFromStripe,
        maxUsers: limitInfo.maxUsers,
        trialEndDate: subscriptionStatus.trialEnd || limitInfo.trialEndDate,
      }));
    }
  }, [
    isAuthenticated,
    subscriptionStatus,
    limitInfo,
    loadingSubscription,
    loadingLimitInfo,
    currentPlan,
    currentIsTrialExpired,
    dispatch,
  ]);

  return <>{children}</>;
};

export default SubscriptionSyncProvider;
